import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { routePaths } from "../constants/routes";
import {
  automationFrameworkBlueprint,
  automationScenarioLibrary,
  automationScenarioSuites,
} from "../content/automationScenarioLibrary";
import "../style/automation_testcase.css";

export default function AutomationExercises() {
  const [activeSuite, setActiveSuite] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedScenarioId, setSelectedScenarioId] = useState(automationScenarioLibrary[0]?.id || "");

  const visibleScenarios = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return automationScenarioLibrary.filter((scenario) => {
      const matchesSuite = activeSuite === "all" || scenario.suite === activeSuite;
      const matchesQuery =
        !query ||
        [
          scenario.title,
          scenario.suite,
          scenario.feature,
          scenario.type,
          scenario.difficulty,
          scenario.problemStatement,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);

      return matchesSuite && matchesQuery;
    });
  }, [activeSuite, searchQuery]);

  const selectedScenario =
    visibleScenarios.find((scenario) => scenario.id === selectedScenarioId) ||
    automationScenarioLibrary.find((scenario) => scenario.id === selectedScenarioId) ||
    visibleScenarios[0] ||
    automationScenarioLibrary[0];

  const handleScenarioSelection = (scenarioId) => {
    setSelectedScenarioId(scenarioId);
    const detailSection = document.getElementById("scenario-detail");
    if (detailSection) {
      detailSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="automation-case-library-page">
      <header className="automation-case-hero">
        <div className="automation-case-hero-copy">
          <span className="case-chip">Automation Testcase Library</span>
          <h1>Learn automation from real scenarios, not only from isolated demos</h1>
          <p>
            This page is designed like a project-ready testcase bank. You get {automationScenarioLibrary.length} structured
            automation scenarios covering authentication, ecommerce catalog, checkout, API edge cases, regression,
            and framework design so learners can build their own automation framework from QACoder only.
          </p>
          <div className="automation-case-hero-actions">
            <NavLink to={routePaths.automationTesting} className="case-primary-btn">
              Open Automation Practice Lab
            </NavLink>
            <NavLink to={routePaths.apiTesting} className="case-secondary-btn">
              Learn API Testing
            </NavLink>
          </div>
        </div>

        <div className="automation-case-hero-stats">
          <div>
            <strong>{automationScenarioLibrary.length}</strong>
            <span>Total scenarios</span>
          </div>
          <div>
            <strong>{automationScenarioSuites.length - 1}</strong>
            <span>Core suites</span>
          </div>
          <div>
            <strong>10</strong>
            <span>Execution steps each</span>
          </div>
        </div>
      </header>

      <section className="automation-case-shortcuts">
        <NavLink to={routePaths.automationTesting}>Automation Practice</NavLink>
        <NavLink to={routePaths.automationTestingTopic}>Automation Guide</NavLink>
        <NavLink to={routePaths.apiTesting}>API Testing</NavLink>
        <NavLink to={routePaths.studyNotes}>Study Notes</NavLink>
        <NavLink to={routePaths.jiraNotes}>JIRA Notes</NavLink>
      </section>

      <main className="automation-case-layout">
        <aside className="automation-case-sidebar">
          <div className="case-sidebar-section">
            <h2>Suites</h2>
            <div className="suite-filter-list">
              {automationScenarioSuites.map((suite) => (
                <button
                  key={suite.id}
                  type="button"
                  className={activeSuite === suite.id ? "active" : ""}
                  onClick={() => setActiveSuite(suite.id)}
                >
                  <span>{suite.label}</span>
                  <strong>{suite.count}</strong>
                </button>
              ))}
            </div>
          </div>

          <div className="case-sidebar-section">
            <h2>Search</h2>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search suite, feature, difficulty..."
              aria-label="Search automation scenarios"
            />
          </div>

          <div className="case-sidebar-section">
            <h2>Framework blueprint</h2>
            <ul className="framework-blueprint-list">
              {automationFrameworkBlueprint.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </aside>

        <section className="automation-case-content">
          <div className="case-content-topbar">
            <div>
              <span className="section-kicker">Scenario Browser</span>
              <h2>{visibleScenarios.length} scenarios ready for testcase design</h2>
            </div>
            <p>
              Every scenario includes objective, problem statement, execution steps, expected results, and
              framework learning hints.
            </p>
          </div>

          <div className="scenario-card-grid">
            {visibleScenarios.map((scenario) => (
              <article
                key={scenario.id}
                className={`scenario-card ${selectedScenario?.id === scenario.id ? "selected" : ""}`}
              >
                <div className="scenario-card-top">
                  <span>{scenario.suite}</span>
                  <small>{scenario.difficulty}</small>
                </div>
                <h3>{scenario.title}</h3>
                <div className="scenario-card-meta">
                  <span>{scenario.type}</span>
                  <span>{scenario.feature}</span>
                  {scenario.module ? <span>{scenario.module}</span> : null}
                </div>
                {scenario.goal ? <strong className="scenario-goal-line">{scenario.goal}</strong> : null}
                <p>{scenario.problemStatement}</p>
                <button type="button" onClick={() => handleScenarioSelection(scenario.id)}>
                  View {scenario.executionSteps.length} execution steps
                </button>
              </article>
            ))}
          </div>

          {selectedScenario && (
            <article className="scenario-detail-panel" id="scenario-detail">
              <div className="scenario-detail-head">
                <div>
                  <span className="section-kicker">{selectedScenario.suite}</span>
                  <h2>{selectedScenario.title}</h2>
                </div>
                <div className="detail-badges">
                  <span>{selectedScenario.difficulty}</span>
                  <span>{selectedScenario.type}</span>
                  <span>{selectedScenario.feature}</span>
                </div>
              </div>

              <div className="detail-info-grid">
                <div className="detail-card">
                  <h3>Problem statement</h3>
                  <p>{selectedScenario.problemStatement}</p>
                </div>
                <div className="detail-card">
                  <h3>Precondition</h3>
                  <p>{selectedScenario.precondition}</p>
                </div>
                {selectedScenario.goal ? (
                  <div className="detail-card">
                    <h3>Goal</h3>
                    <p>{selectedScenario.goal}</p>
                  </div>
                ) : null}
                {selectedScenario.module ? (
                  <div className="detail-card">
                    <h3>Module</h3>
                    <p>{selectedScenario.module}</p>
                  </div>
                ) : null}
              </div>

              <div className="detail-grid">
                <div className="detail-card wide">
                  <h3>Execution steps</h3>
                  <ol>
                    {selectedScenario.executionSteps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                </div>

                <div className="detail-card">
                  <h3>Expected results</h3>
                  <ul>
                    {selectedScenario.expectedResults.map((result) => (
                      <li key={result}>{result}</li>
                    ))}
                  </ul>
                </div>

                <div className="detail-card">
                  <h3>Locator hints</h3>
                  <ul>
                    {selectedScenario.locatorHints.map((hint) => (
                      <li key={hint}>{hint}</li>
                    ))}
                  </ul>
                </div>

                <div className="detail-card">
                  <h3>Selenium hint</h3>
                  <p>{selectedScenario.seleniumHint}</p>
                </div>

                <div className="detail-card">
                  <h3>Playwright hint</h3>
                  <p>{selectedScenario.playwrightHint}</p>
                </div>
              </div>
            </article>
          )}
        </section>
      </main>
    </div>
  );
}
